import { useEffect, useMemo, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Drawer } from 'vaul'
import { toast } from 'sonner'
import { ArrowLeft, Heart, Lock } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import TierCard from './TierCard'
import CartSummary from './CartSummary'
import DonationPicker, { type DonationInterval } from './DonationPicker'
import FreeCheckoutModal from './FreeCheckoutModal'
import { formatDollars, grossUpStripeFee } from '@/lib/format'
import type { Event, PurchasableItem, SharedProps } from '@/types'

const FEE_HELPER_TEXT =
  'Covering the fee means 100% of your donation goes to the organization'
const ACCOUNT_OPT_IN_TEXT =
  'Never lose your tickets, keep track of donations, get notified of event updates and more when you become a philanthropist'

const MAX_PER_TIER = 20

type Props = {
  event: Event
  items: PurchasableItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Step = 'tiers' | 'donation-addon' | 'review' | 'donation-only'

type Section = {
  kind: 'ticket' | 'sponsorship' | 'ad'
  title: string
  items: PurchasableItem[]
}

const SECTION_ORDER: Array<{ kind: 'ticket' | 'sponsorship' | 'ad'; title: string }> = [
  { kind: 'ticket', title: 'Tickets' },
  { kind: 'sponsorship', title: 'Sponsorships' },
  { kind: 'ad', title: 'Program ads' },
]

function buildSections(items: PurchasableItem[]): Section[] {
  return SECTION_ORDER.flatMap(({ kind, title }) => {
    const filtered = items.filter((i) => i.kind === kind)
    if (filtered.length === 0) return []
    return [{ kind, title, items: [...filtered].sort((a, b) => sortKey(a) - sortKey(b)) }]
  })
}

function tierSubtitle(item: PurchasableItem): string | undefined {
  if (item.kind !== 'ad') return undefined
  return item.ad_format === 'digital' ? 'Digital' : 'Print'
}

function useIsMobile(query = '(max-width: 767px)') {
  const [match, setMatch] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia(query)
    const update = () => setMatch(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [query])
  return match
}

export default function CartModal({ event, items, open, onOpenChange }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const sections = useMemo(() => buildSections(items), [items])
  const flatItems = useMemo(() => sections.flatMap((s) => s.items), [sections])
  const [cart, setCart] = useState<Record<string, number>>({})
  const [step, setStep] = useState<Step>('tiers')
  const [donationCents, setDonationCents] = useState<number | null>(null)
  const [donateOnlyCents, setDonateOnlyCents] = useState<number | null>(null)
  const [donateOnlyInterval, setDonateOnlyInterval] = useState<DonationInterval>('one_time')
  const [coverFee, setCoverFee] = useState(true)
  const [wantsAccount, setWantsAccount] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [freeOpen, setFreeOpen] = useState(false)
  const isMobile = useIsMobile()

  const donationsEnabled = event.donations_enabled === true
  const showAccountOptIn = !current_user

  useEffect(() => {
    if (!open) {
      setCart({})
      setStep('tiers')
      setDonationCents(null)
      setDonateOnlyCents(null)
      setDonateOnlyInterval('one_time')
      setCoverFee(true)
      setWantsAccount(true)
      setSubmitting(false)
      setFreeOpen(false)
    }
  }, [open])

  const lines = flatItems.flatMap((item) => {
    const qty = cart[item.id] ?? 0
    return qty > 0 ? [{ item, quantity: qty }] : []
  })
  const subtotalCents = lines.reduce((acc, l) => acc + l.item.price_cents * l.quantity, 0)
  const isFreeCart = lines.length > 0 && subtotalCents === 0
  const summaryDonation = step === 'donation-addon' || step === 'review' ? donationCents : null
  // Fee is grossed up on the combined buyer total (subtotal + any donation
  // being added in the addon step). Free carts skip Stripe entirely so the
  // gross-up is suppressed and the server explicitly ignores cover_fee at $0.
  const feeBasisCents = subtotalCents + (summaryDonation ?? 0)
  const { feeCents, grossCents } =
    isFreeCart ? { feeCents: 0, grossCents: 0 } : grossUpStripeFee(feeBasisCents)
  // Only the Review step discloses the processing-fee gross-up; earlier
  // sidebars show the buyer-facing subtotal so the fee isn't a surprise add-on
  // before the buyer has reached the review/pay screen.
  const onReview = step === 'review'
  const totalCents = isFreeCart ? 0 : onReview && coverFee ? grossCents : feeBasisCents
  const donateOnlyMonthly = donateOnlyInterval === 'monthly'
  // Cover-the-fee is one-time only; monthly gifts charge the exact amount.
  const donateOnlyCoverFee = donateOnlyMonthly ? false : coverFee
  const donateOnlyFee = grossUpStripeFee(donateOnlyCents ?? 0)
  const donateOnlyTotalCents = donateOnlyCents == null
    ? null
    : donateOnlyCoverFee ? donateOnlyFee.grossCents : donateOnlyCents

  function handleSoldOut(purchasableItemId: string, remaining: number | null, name: string) {
    const cap = remaining ?? 0
    // Cap the offending tier to its actual remaining seats so the buyer
    // can adjust without guessing. cap=0 zeros the row out entirely.
    setCart((c) => ({ ...c, [purchasableItemId]: cap }))
    setStep('tiers')
    if (remaining === 0) {
      toast.error(`${name} just sold out. We've removed it from your cart.`)
    } else if (typeof remaining === 'number') {
      toast.error(`${name} only has ${remaining} left. We've adjusted your cart.`)
    } else {
      toast.error(`${name} just sold out. Adjust your cart and try again.`)
    }
  }

  async function submitCombined(includeDonation: boolean) {
    if (submitting || lines.length === 0) return
    if (isFreeCart) {
      setFreeOpen(true)
      return
    }
    setSubmitting(true)
    try {
      const csrf = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? ''
      const body: Record<string, unknown> = {
        items: lines.map((l) => ({ purchasable_item_id: l.item.id, quantity: l.quantity })),
        cover_processing_fee: coverFee,
      }
      if (showAccountOptIn) body.wants_account = wantsAccount
      if (includeDonation && donationCents != null && donationCents > 0) {
        body.donation_amount_cents = donationCents
      }
      const res = await fetch('/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(body),
      })
      const data = (await res.json()) as {
        url?: string
        error?: string
        purchasable_item_id?: string
        purchasable_item_name?: string
        remaining?: number | null
        max_per_buyer?: number
      }
      if (data.url) {
        window.location.assign(data.url)
        return
      }
      if (data.error === 'sold_out' && data.purchasable_item_id) {
        handleSoldOut(data.purchasable_item_id, data.remaining ?? null, data.purchasable_item_name ?? 'A tier')
      } else if (data.error === 'over_max_per_buyer') {
        toast.error(
          `${data.purchasable_item_name ?? 'This tier'} is limited to ${data.max_per_buyer} per transaction.`,
        )
      } else {
        toast.error("We couldn't start checkout. Please try again or contact the organizer.")
      }
    } catch {
      toast.error("We couldn't start checkout. Please try again or contact the organizer.")
    }
    setSubmitting(false)
  }

  async function submitDonationOnly() {
    if (submitting || donateOnlyCents == null) return
    setSubmitting(true)
    try {
      const csrf = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? ''
      const donationBody: Record<string, unknown> = {
        event_id: event.id,
        amount_cents: donateOnlyCents,
        interval: donateOnlyInterval,
        cover_processing_fee: donateOnlyCoverFee,
      }
      if (showAccountOptIn) donationBody.wants_account = wantsAccount
      const res = await fetch('/checkout/donation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(donationBody),
      })
      const data = (await res.json()) as { url?: string; error?: string }
      if (data.url) {
        window.location.assign(data.url)
        return
      }
      toast.error("We couldn't start checkout. Please try again or contact the organizer.")
    } catch {
      toast.error("We couldn't start checkout. Please try again or contact the organizer.")
    }
    setSubmitting(false)
  }

  function continueFromTiers() {
    if (lines.length === 0) return
    if (isFreeCart) {
      setFreeOpen(true)
      return
    }
    if (donationsEnabled) {
      setStep('donation-addon')
    } else {
      setStep('review')
    }
  }

  function openDonationOnly() {
    setDonateOnlyCents(null)
    setStep('donation-only')
  }

  function goBack() {
    if (step === 'review') {
      setStep(donationsEnabled ? 'donation-addon' : 'tiers')
    } else {
      setStep('tiers')
    }
  }

  const eventDateLabel = formatDate(event.date)

  const accountOptIn = showAccountOptIn ? (
    <label className="flex items-start gap-2.5 rounded-lg border border-zinc-950/10 bg-muted/30 p-3 text-sm">
      <Checkbox
        checked={wantsAccount}
        onCheckedChange={(v) => setWantsAccount(v === true)}
        aria-label="Create an account after checkout"
        className="mt-0.5"
      />
      <span className="text-foreground">{ACCOUNT_OPT_IN_TEXT}</span>
    </label>
  ) : null

  const tierList = (
    <div className="space-y-5">
      {sections.map((section) => (
        <div key={section.kind} className="space-y-2.5">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {section.title}
          </h3>
          <div className="space-y-2.5">
            {section.items.map((item) => (
              <TierCard
                key={item.id}
                item={item}
                quantity={cart[item.id] ?? 0}
                onChange={(next) => setCart((c) => ({ ...c, [item.id]: next }))}
                maxPerTier={MAX_PER_TIER}
                subtitle={tierSubtitle(item)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  const donationAddonBody = (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Heart className="size-4 text-primary" fill="currentColor" />
        <h3 className="text-base font-semibold">Add a donation?</h3>
      </div>
      <DonationPicker amountCents={donationCents} onChange={setDonationCents} />
    </div>
  )

  const donationOnlyBody = (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Heart className="size-4 text-primary" fill="currentColor" />
        <h3 className="text-base font-semibold">Make a donation</h3>
      </div>
      <DonationPicker
        amountCents={donateOnlyCents}
        onChange={setDonateOnlyCents}
        interval={donateOnlyInterval}
        onIntervalChange={setDonateOnlyInterval}
      />
      {donateOnlyCents != null && (
        <div className="space-y-3 rounded-lg border border-zinc-950/10 p-3 text-sm">
          {!donateOnlyMonthly && (
            <div className="space-y-1.5">
              <label className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={coverFee}
                    onCheckedChange={(v) => setCoverFee(v === true)}
                    aria-label="Cover processing fee"
                  />
                  <span className="text-foreground">Cover processing fee</span>
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {formatDollars(donateOnlyFee.feeCents)}
                </span>
              </label>
              <p className="pl-6 text-xs text-muted-foreground">{FEE_HELPER_TEXT}</p>
            </div>
          )}
          <div className="flex items-baseline justify-between border-t border-zinc-950/10 pt-3">
            <span className="text-muted-foreground">{donateOnlyMonthly ? 'Per month' : 'Total'}</span>
            <span className="font-semibold tabular-nums">
              {formatDollars(donateOnlyTotalCents ?? donateOnlyCents)}
              {donateOnlyMonthly && <span className="text-muted-foreground"> / mo</span>}
            </span>
          </div>
        </div>
      )}
      {donateOnlyCents != null && accountOptIn}
      <Button
        type="button"
        className="w-full"
        onClick={submitDonationOnly}
        disabled={submitting || donateOnlyCents == null}
      >
        {submitting
          ? 'Starting checkout…'
          : donateOnlyTotalCents != null
            ? `Donate ${formatDollars(donateOnlyTotalCents)}${donateOnlyMonthly ? ' / month' : ''}`
            : 'Donate'}
      </Button>
    </div>
  )

  const tierActions = (
    <>
      <Button
        type="button"
        onClick={continueFromTiers}
        disabled={lines.length === 0 || submitting}
        title={lines.length === 0 ? 'Add at least one item' : undefined}
        className="w-full"
      >
        {submitting
          ? 'Starting checkout…'
          : isFreeCart ? 'Claim free tickets' : 'Continue'}
      </Button>
      {donationsEnabled && !isFreeCart && (
        <button
          type="button"
          onClick={openDonationOnly}
          className="text-sm text-muted-foreground underline-offset-2 transition hover:text-foreground hover:underline"
        >
          I'd just like to donate
        </button>
      )}
    </>
  )

  const donationAddonActions = (
    <>
      <Button
        type="button"
        onClick={() => setStep('review')}
        disabled={submitting || donationCents == null || donationCents <= 0}
        className="w-full"
      >
        {donationCents
          ? `Add ${formatDollars(donationCents)} & continue`
          : 'Add donation & continue'}
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          setDonationCents(null)
          setStep('review')
        }}
        disabled={submitting}
        className="w-full"
      >
        Continue without donating
      </Button>
    </>
  )

  const reviewBody = (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">Review your order</h3>
      <CartSummary
        lines={lines}
        feeCents={feeCents}
        totalCents={totalCents}
        donationCents={summaryDonation}
        coverFee={coverFee}
        onCoverFeeChange={setCoverFee}
        isFreeCart={isFreeCart}
        actions={null}
        feeHelperText={FEE_HELPER_TEXT}
      />
      {accountOptIn}
      <Button
        type="button"
        onClick={() => submitCombined(donationCents != null && donationCents > 0)}
        disabled={submitting}
        className="w-full"
      >
        {submitting ? 'Starting checkout…' : `Pay ${formatDollars(totalCents)}`}
      </Button>
      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="size-3" /> Secure checkout via Stripe
      </p>
    </div>
  )

  const headerLeft = step === 'tiers' ? null : (
    <button
      type="button"
      onClick={goBack}
      aria-label="Back"
      className="mr-3 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
    </button>
  )

  const headerTitle = (
    <div className="flex items-center">
      {headerLeft}
      <div className="min-w-0">
        <DialogTitle className="text-base font-semibold tracking-tight">{event.title}</DialogTitle>
        {eventDateLabel && (
          <p className="mt-0.5 text-sm text-muted-foreground">{eventDateLabel}</p>
        )}
      </div>
    </div>
  )

  const freeModal = (
    <FreeCheckoutModal
      open={freeOpen}
      onOpenChange={setFreeOpen}
      lines={lines}
      onSoldOut={handleSoldOut}
    />
  )

  // The donation-only view drops the cart sidebar to keep the modal focused on
  // the gift; the cart contents stay in state so the Back arrow restores them.
  const showSidebar = step === 'tiers' || step === 'donation-addon'
  const leftBody =
    step === 'tiers'
      ? tierList
      : step === 'donation-addon'
        ? donationAddonBody
        : step === 'review'
          ? reviewBody
          : donationOnlyBody
  const actions = step === 'tiers' ? tierActions : step === 'donation-addon' ? donationAddonActions : null

  if (isMobile) {
    return (
      <>
        <Drawer.Root open={open} onOpenChange={onOpenChange}>
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 z-50 bg-zinc-950/50" />
            <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 mt-24 flex h-[92%] flex-col rounded-t-2xl bg-background outline-none">
              <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-zinc-950/10" aria-hidden />
              <div className="flex items-center px-5 pt-3 pb-1">
                {headerLeft}
                <div className="min-w-0">
                  <Drawer.Title className="text-base font-semibold tracking-tight">
                    {event.title}
                  </Drawer.Title>
                  {eventDateLabel && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{eventDateLabel}</p>
                  )}
                </div>
              </div>
              <div className={`flex-1 overflow-y-auto px-5 pt-3 ${showSidebar ? 'pb-32' : 'pb-6'}`}>
                {leftBody}
              </div>
              {showSidebar && (
                <div className="sticky inset-x-0 bottom-0 border-t border-zinc-950/10 bg-background px-5 py-4">
                  <CartSummary
                    lines={lines}
                    feeCents={feeCents}
                    totalCents={totalCents}
                    donationCents={summaryDonation}
                    coverFee={coverFee}
                    onCoverFeeChange={setCoverFee}
                    isFreeCart={isFreeCart}
                    actions={actions}
                    showFeeRow={false}
                  />
                </div>
              )}
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
        {freeModal}
      </>
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-3xl">
          <div className="border-b border-zinc-950/10 px-6 py-4">{headerTitle}</div>
          {showSidebar ? (
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-[1fr_280px]">
              <div className="max-h-[60vh] overflow-y-auto px-6 py-5 sm:border-r sm:border-zinc-950/10">
                {leftBody}
              </div>
              <div className="px-6 py-5">
                <CartSummary
                  lines={lines}
                  feeCents={feeCents}
                  totalCents={totalCents}
                  donationCents={summaryDonation}
                  coverFee={coverFee}
                  onCoverFeeChange={setCoverFee}
                  isFreeCart={isFreeCart}
                  actions={actions}
                  showFeeRow={false}
                />
              </div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-md px-6 py-6">{leftBody}</div>
          )}
        </DialogContent>
      </Dialog>
      {freeModal}
    </>
  )
}

function sortKey(item: PurchasableItem) {
  const perPurchase = Math.max(1, item.tickets_per_purchase)
  const remaining =
    item.inventory_limit != null
      ? Math.floor(Math.max(0, item.inventory_limit - item.inventory_sold) / perPurchase)
      : Number.POSITIVE_INFINITY
  // Sold-out tiers sink to the bottom; otherwise sort by price ascending.
  return remaining <= 0 ? Number.POSITIVE_INFINITY : item.price_cents
}

function formatDate(date: string | null) {
  if (!date) return null
  try {
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date)
    return new Date(isDateOnly ? `${date}T00:00:00` : date).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    })
  } catch {
    return null
  }
}
